'use client';

import Loading from '@dr-pam/common-components/Components/Misc/Loading';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { useGoogleMapsService } from '@dr-pam/common-components/Services/GoogleMapsService';
import React, { useEffect, useRef, useState } from 'react';

export type GoogleMapMarker = {
	id: string;
	location: google.maps.LatLngLiteral;
	title: string;
	infoWindow?: string;
};

export type GoogleMapMarkerMap = Map<
	GoogleMapMarker,
	{
		marker: google.maps.Marker;
		infoWindow: google.maps.InfoWindow;
	}
>;

export type GoogleMapProps = {
	className?: string;
	centerOn?: google.maps.LatLngLiteral;
	initialPositionMarker?: google.maps.LatLngLiteral;
	initialPositionRadiusKm?: number;
	markers?: GoogleMapMarker[];
	selectedMarker?: GoogleMapMarker;
	onMarkerChanged?: (marker: GoogleMapMarker | null) => void;
};

export default function GoogleMap(props: GoogleMapProps) {
	const {
		className,
		centerOn,
		initialPositionMarker,
		initialPositionRadiusKm,
		markers,
		selectedMarker,
		onMarkerChanged,
	} = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const googleMapsService = useGoogleMapsService();

	const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
	const googleMapsMarkerMap = useRef<GoogleMapMarkerMap>(new Map());

	const [divRef, setDivRef] = useState<HTMLDivElement | null>(null);
	const [map, setMap] = useState<google.maps.Map | null>(null);

	// Re-center the map when the initial position changes
	useEffect(() => {
		if (map && centerOn) {
			map.setCenter(centerOn);
		}
	}, [centerOn, map]);

	// Open the info window for the selected marker
	useEffect(() => {
		if (map && selectedMarker) {
			const mapItem = googleMapsMarkerMap.current.get(selectedMarker);
			if (mapItem) {
				// Close all other info windows first (and make sure there's no animation)...
				Array.from(googleMapsMarkerMap.current.values()).forEach((item) => {
					item.infoWindow.close();
					item.marker.setAnimation(null);
				});

				// ...then open this one (and bounce the marker fora  bit)
				mapItem.infoWindow.open({
					anchor: mapItem.marker,
					map,
					shouldFocus: true,
				});
				mapItem.marker.setAnimation(google.maps.Animation.BOUNCE);
				setTimeout(() => {
					mapItem.marker.setAnimation(null);
				}, 3000);
			}
		}
	}, [map, selectedMarker]);

	// Renders the map whenever it needs to
	useEffect(() => {
		if (!isGoogleLoaded || !divRef) {
			return;
		}

		const googleMap = new google.maps.Map(divRef, {
			zoom: 9,
			center: centerOn,
		});

		if (initialPositionMarker) {
			new google.maps.Marker({
				map: googleMap,
				position: initialPositionMarker,
				title: 'Your position',
				icon: {
					path: google.maps.SymbolPath.CIRCLE,
					scale: 10,
					fillColor: '#755773',
					fillOpacity: 0.8,
					strokeWeight: 1,
				},
			});
		}

		if (initialPositionMarker && initialPositionRadiusKm) {
			new google.maps.Circle({
				map: googleMap,
				center: initialPositionMarker,
				radius: initialPositionRadiusKm * 1000,
				strokeColor: '#755773',
				strokeOpacity: 0.8,
				strokeWeight: 2,
				fillColor: '#755773',
				fillOpacity: 0.2,
			});
		}

		if (markers) {
			googleMapsMarkerMap.current.clear();

			for (const m of markers) {
				const marker = new google.maps.Marker({
					map: googleMap,
					title: m.title,
					position: m.location,
				});

				const infoWindow = new google.maps.InfoWindow({
					content: m.infoWindow,
				});

				infoWindow.addListener('closeclick', () => {
					onMarkerChanged?.(null);
				});

				marker.addListener('click', () => {
					// Hide all other info windows
					Array.from(googleMapsMarkerMap.current.values()).forEach((item) => {
						item.infoWindow.close();
						item.marker.setAnimation(null);
					});
					infoWindow.open(googleMap, marker);
					onMarkerChanged?.(m);
				});

				googleMapsMarkerMap.current.set(m, {
					marker,
					infoWindow,
				});
			}
		}

		setMap(googleMap);
	}, [centerOn, divRef, initialPositionMarker, initialPositionRadiusKm, isGoogleLoaded, markers, onMarkerChanged]);

	// Waits for the google maps api to load
	useEffect(() => {
		const loader = addLoader();
		googleMapsService
			.waitForGoogleMapsApi()
			.then(() => {
				setIsGoogleLoaded(true);
			})
			.catch(() => {})
			.finally(() => removeLoader(loader));
	}, [addLoader, googleMapsService, removeLoader]);

	return (
		<>
			{isLoading && <Loading />}
			<div
				ref={setDivRef}
				className={`GoogleMap ${className ?? ''}`}
				style={{ visibility: isLoading ? 'hidden' : 'visible' }}
			></div>
		</>
	);
}
